import React, { memo, useMemo } from "react";
import { Star, Trash2, SearchX, ChevronRight } from "lucide-react";
import { groupByCategory } from "../../utils/deviceUtils";
import { CATEGORY_LABELS } from "../../constants/previewConfig";

const DeviceRow = memo(function DeviceRow({ device, selected, favorite, onToggle, onToggleFavorite, onDelete }) {
  return (
    <div className={`device-row ${selected ? "device-row-active" : ""}`}>
      <button
        type="button"
        className="device-row-main"
        aria-pressed={selected}
        onClick={() => onToggle(device.id)}
      >
        <span className="device-row-name">{device.name}</span>
        <span className="device-row-dims">
          {device.width} × {device.height}
          {device.releaseYear ? ` · ${device.releaseYear}` : ""}
        </span>
      </button>
      <button
        type="button"
        className={`icon-btn favorite-btn ${favorite ? "favorite-btn-active" : ""}`}
        aria-label={favorite ? `Remove ${device.name} from favorites` : `Add ${device.name} to favorites`}
        aria-pressed={favorite}
        onClick={() => onToggleFavorite(device.id)}
      >
        <Star size={15} strokeWidth={1.75} fill={favorite ? "currentColor" : "none"} aria-hidden="true" />
      </button>
      {device.isCustom && (
        <button
          type="button"
          className="icon-btn"
          aria-label={`Delete custom device ${device.name}`}
          onClick={() => onDelete(device.id)}
        >
          <Trash2 size={15} strokeWidth={1.75} aria-hidden="true" />
        </button>
      )}
    </div>
  );
});

function DeviceGroup({ title, devices, defaultOpen, selectedDeviceIds, favoriteIds, onToggle, onToggleFavorite, onDelete }) {
  if (devices.length === 0) return null;
  return (
    <details className="device-group" open={defaultOpen}>
      <summary>
        <ChevronRight className="device-group-chevron" size={15} strokeWidth={2} aria-hidden="true" />
        {title} <span className="device-group-count">{devices.length}</span>
      </summary>
      <div className="device-group-list">
        {devices.map((device) => (
          <DeviceRow
            key={device.id}
            device={device}
            selected={selectedDeviceIds.includes(device.id)}
            favorite={favoriteIds.includes(device.id)}
            onToggle={onToggle}
            onToggleFavorite={onToggleFavorite}
            onDelete={onDelete}
          />
        ))}
      </div>
    </details>
  );
}

function DeviceList({
  devices,
  favoriteDevices,
  recentDevices,
  selectedDeviceIds,
  favoriteIds,
  onToggle,
  onToggleFavorite,
  onDelete,
  onSelectAll,
  onClearSelection,
  onResetFilters,
  isFiltered,
}) {
  const featured = useMemo(() => devices.filter((d) => d.featured), [devices]);
  const grouped = useMemo(() => groupByCategory(devices), [devices]);

  if (devices.length === 0) {
    return (
      <div className="no-results">
        <SearchX size={28} strokeWidth={1.5} aria-hidden="true" />
        <p>No devices match your search.</p>
        {isFiltered && (
          <button type="button" className="link-btn" onClick={onResetFilters}>
            Reset filters
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="device-list">
      <div className="device-list-actions">
        <span className="selected-count" aria-live="polite">
          {selectedDeviceIds.length} selected
        </span>
        <div className="device-list-action-buttons">
          <button type="button" className="link-btn" onClick={() => onSelectAll(devices.map((d) => d.id))}>
            Select all
          </button>
          <button type="button" className="link-btn" onClick={onClearSelection}>
            Clear selection
          </button>
          {isFiltered && (
            <button type="button" className="link-btn" onClick={onResetFilters}>
              Reset filters
            </button>
          )}
        </div>
      </div>

      {!isFiltered && favoriteDevices.length > 0 && (
        <DeviceGroup
          title="Favorites"
          devices={favoriteDevices}
          defaultOpen
          selectedDeviceIds={selectedDeviceIds}
          favoriteIds={favoriteIds}
          onToggle={onToggle}
          onToggleFavorite={onToggleFavorite}
          onDelete={onDelete}
        />
      )}

      {!isFiltered && recentDevices.length > 0 && (
        <DeviceGroup
          title="Recently used"
          devices={recentDevices}
          defaultOpen={false}
          selectedDeviceIds={selectedDeviceIds}
          favoriteIds={favoriteIds}
          onToggle={onToggle}
          onToggleFavorite={onToggleFavorite}
          onDelete={onDelete}
        />
      )}

      {!isFiltered && featured.length > 0 && (
        <DeviceGroup
          title="Featured"
          devices={featured}
          defaultOpen
          selectedDeviceIds={selectedDeviceIds}
          favoriteIds={favoriteIds}
          onToggle={onToggle}
          onToggleFavorite={onToggleFavorite}
          onDelete={onDelete}
        />
      )}

      {Object.entries(grouped).map(([category, list], index) => (
        <DeviceGroup
          key={category}
          title={CATEGORY_LABELS[category] || category}
          devices={list}
          defaultOpen={isFiltered || index === 0}
          selectedDeviceIds={selectedDeviceIds}
          favoriteIds={favoriteIds}
          onToggle={onToggle}
          onToggleFavorite={onToggleFavorite}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

export default memo(DeviceList);
